<template>
  <AppLoading v-if="!appReady" />

  <template v-else-if="isAdminRoute">
    <div class="admin-root">
      <RouterView v-slot="{ Component, route: adminRoute }">
        <Suspense timeout="0">
          <component :is="Component" :key="adminRoute.fullPath" class="route-view" />
          <template #fallback>
            <AppLoading />
          </template>
        </Suspense>
      </RouterView>
      <AppLoading v-if="routeNavigating" overlay />
    </div>
  </template>

  <div v-else class="app-shell portal-v2">
    <SiteHeader />
    <main class="app-main">
      <RouterView v-slot="{ Component, route: pageRoute }">
        <Transition name="page-fade" mode="out-in">
          <component :is="Component" :key="pageRoute.path" class="route-view" />
        </Transition>
      </RouterView>
    </main>
    <SiteFooter />
    <SiteToast />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, RouterView } from 'vue-router';
import AOS from 'aos';
import AppLoading from './components/AppLoading.vue';
import SiteFooter from './components/SiteFooter.vue';
import SiteHeader from './components/SiteHeader.vue';
import SiteToast from './components/SiteToast.vue';
import { bootstrapFrontCms } from './composables/useFrontCms';
import router from './router';

const route = useRoute();
const appReady = ref(false);
const routeNavigating = ref(false);

/** 与 window.location 一致，避免路由未就绪时误渲染前台壳 */
const isAdminRoute = computed(() => {
  if (!appReady.value) {
    return typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
  }
  return route.path.startsWith('/admin');
});

let frontCmsBootstrapped = false;

router.beforeEach((_to, from) => {
  if (from.matched.length) {
    routeNavigating.value = true;
  }
});

router.afterEach((to) => {
  nextTick(() => {
    routeNavigating.value = false;
    if (!to.path.startsWith('/admin')) {
      AOS.refresh();
    }
  });

  if (to.path.startsWith('/admin') || frontCmsBootstrapped) {
    return;
  }
  frontCmsBootstrapped = true;
  bootstrapFrontCms();
});

onMounted(async () => {
  await router.isReady();
  appReady.value = true;
});
</script>

<style scoped>
.admin-root {
  position: relative;
  min-height: 100vh;
}

.app-main {
  position: relative;
  width: 100%;
}

.route-view {
  width: 100%;
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.22s ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
</style>
